import { BrandCheckSchemas } from "@mg/schemas";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect } from "react";

import * as BrandCheckService from "../../services/brandcheck";

/**
 * React Query hooks for BrandCheck API
 *
 * Provides type-safe hooks for all BrandCheck operations with caching,
 * error handling, and optimistic updates.
 */

// Query keys for cache management
export const brandCheckKeys = {
  all: ["brandcheck"] as const,
  sessions: () => [...brandCheckKeys.all, "sessions"] as const,
  session: (sessionId: string) =>
    [...brandCheckKeys.sessions(), sessionId] as const,
  research: (sessionId: string) =>
    [...brandCheckKeys.session(sessionId), "research"] as const,
  results: (sessionId: string) =>
    [...brandCheckKeys.session(sessionId), "results"] as const,
  chat: (sessionId: string) =>
    [...brandCheckKeys.session(sessionId), "chat"] as const,
  health: () => [...brandCheckKeys.all, "health"] as const,
} as const;

/**
 * Create or get brandcheck session
 */
export function useCreateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: BrandCheckService.createOrGetSession,
    onSuccess: (data) => {
      // Cache the session data
      queryClient.setQueryData(brandCheckKeys.session(data.session_id), data);
    },
    onError: (error) => {
      console.error("Failed to create brandcheck session:", error);
    },
  });
}

/**
 * Get research progress with automatic polling
 */
export function useResearchProgress(
  sessionId: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    onCompleted?: (data: BrandCheckSchemas.ResearchResponse) => void;
  },
) {
  const query = useQuery({
    queryKey: brandCheckKeys.research(sessionId || ""),
    queryFn: () => BrandCheckService.getResearchProgress(sessionId!),
    enabled: !!sessionId && options?.enabled !== false,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Auto-poll if research is in progress
      if (data?.status === "researching") {
        return options?.refetchInterval || 2000; // Poll every 2 seconds
      }
      return false; // Stop polling when complete/failed
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Handle completion callback
  useEffect(() => {
    if (query.data?.status === "completed" && options?.onCompleted) {
      options.onCompleted(query.data);
    }
  }, [query.data?.status, options?.onCompleted, query.data]);

  return query;
}

/**
 * Get analysis results
 */
export function useAnalysisResults(sessionId: string | null) {
  return useQuery({
    queryKey: [`anal-res-${sessionId}`],
    queryFn: () => BrandCheckService.getAnalysisResults(sessionId!),
    enabled: !!sessionId,
  });
}

/**
 * Chat with AI agent
 */
export function useChatWithAgent() {
  return useMutation({
    mutationFn: BrandCheckService.chatWithAgent,
  });
}

/**
 * Get chat history
 */
export function useChatHistory(
  sessionId: string | null,
  _options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: brandCheckKeys.chat(sessionId || ""),
    queryFn: async () => {
      // Chat history is obtained by making a chat request with empty message
      // or we could add a dedicated endpoint if needed
      const _results = await BrandCheckService.getAnalysisResults(sessionId!);

      // For now, return empty chat - in practice you might want to store chat separately
      return {
        session_id: sessionId!,
        chat_messages: [],
        total_messages: 0,
      } as {
        session_id: string;
        chat_messages: BrandCheckSchemas.ChatMessage[];
        total_messages: number;
      };
    },
    enabled: !!sessionId && _options?.enabled !== false,
    staleTime: 60 * 1000, // Chat updates frequently, 1 minute stale time
  });
}

/**
 * Service health check
 */
export function useServiceHealth() {
  return useQuery({
    queryKey: brandCheckKeys.health(),
    queryFn: BrandCheckService.getServiceHealth,
    staleTime: 5 * 60 * 1000, // Health status cached for 5 minutes
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Complete brandcheck workflow hook
 *
 * Handles the entire workflow: session -> research -> results
 */
export function useCompleteBrandCheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      input,
      onResearchProgress,
    }: {
      input: Parameters<typeof BrandCheckService.runCompleteBrandCheck>[0];
      onResearchProgress?: Parameters<
        typeof BrandCheckService.runCompleteBrandCheck
      >[1];
    }) => BrandCheckService.runCompleteBrandCheck(input, onResearchProgress),
    onSuccess: (data) => {
      // Cache all the results
      queryClient.setQueryData(
        brandCheckKeys.session(data.session.session_id),
        data.session,
      );
      queryClient.setQueryData(
        brandCheckKeys.research(data.session.session_id),
        data.research,
      );
      queryClient.setQueryData(
        brandCheckKeys.results(data.session.session_id),
        data.results,
      );
    },
  });
}

/**
 * Custom hook for managing brandcheck state
 */
export function useBrandCheckState() {
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<
    "session" | "research" | "results" | "chat"
  >("session");

  const createSession = useCreateSession();
  const research = useResearchProgress(currentSessionId, {
    onCompleted: () => {
      setCurrentStep("results");
    },
  });
  const results = useAnalysisResults(currentSessionId);
  const chatMutation = useChatWithAgent();

  const startBrandCheck = useCallback(
    async (input: BrandCheckSchemas.SessionRequest) => {
      try {
        const session = await createSession.mutateAsync(input);
        setCurrentSessionId(session.session_id);
        setCurrentStep("research");
        return session;
      } catch (error) {
        console.error("Failed to start brand check:", error);
        throw error;
      }
    },
    [createSession],
  );

  const sendChatMessage = useCallback(
    async (message: string) => {
      if (!currentSessionId) {
        throw new Error("No active session");
      }

      return chatMutation.mutateAsync({
        session_id: currentSessionId,
        message,
      });
    },
    [currentSessionId, chatMutation],
  );

  const resetSession = useCallback(() => {
    setCurrentSessionId(null);
    setCurrentStep("session");
  }, []);

  return {
    // State
    currentSessionId,
    currentStep,

    // Data
    session: createSession.data,
    research: research.data,
    results: results.data,

    // Loading states
    isCreatingSession: createSession.isPending,
    isResearching: research.data?.status === "researching",
    isLoadingResults: results.isLoading,
    isSendingMessage: chatMutation.isPending,

    // Error states
    sessionError: createSession.error,
    researchError: research.error,
    resultsError: results.error,
    chatError: chatMutation.error,

    // Actions
    startBrandCheck,
    sendChatMessage,
    resetSession,

    // Utilities
    isComplete: research.data?.status === "completed" && results.data,
    canChat: !!results.data,
    progress: {
      session: !!currentSessionId,
      research: research.data?.status === "completed",
      results: !!results.data,
    },
  };
}
